/* eslint-disable max-lines */
import type { FileRouteTypes } from '@tanstack/react-router';

import { ViteEnv } from '@/env';
import type { AwsCredentialsForList } from '@/lib/queries/awsCredentials';
import type { AwsRole } from '@/lib/queries/awsRole';
import type { GithubConfig } from '@/lib/queries/githubConfig';
import type { S3Bucket } from '@/lib/queries/terraformBackend';
import type { TranslationKeys } from '@/lib/translations/types';

import s3Logo from '../integrations/-components/logos/aws-s3.svg?url';
import awsLogo from '../integrations/-components/logos/aws.svg?url';
import datadogLogo from '../integrations/-components/logos/datadog.svg?url';
import githubLogo from '../integrations/-components/logos/github.svg?url';
import kubernetesLogo from '../integrations/-components/logos/kubernetes.svg?url';

type Requirements =
  | 'aws'
  | 's3Buckets'
  | 'containers'
  | 'github'
  | 'monitoring';

export type RequirementsMap = Record<Requirements, Requirement>;

type Requirement = {
  title: TranslationKeys;
  id: string;
  ok: boolean;
  logoUrl: string;
  onClick?: () => void;
  to?: FileRouteTypes['to'];
  available: boolean;
};

type Question = {
  title: TranslationKeys;
  id: string;
  requirementIds: Requirements[];
};

type Category = {
  id: string;
  title: TranslationKeys;
  questions: Record<string, Question>;
};

export type CatalogMap = Record<string, Category>;

export const getRequirements = ({
  awsCredentials,
  awsRoles,
  githubInstalls,
  s3Buckets,
}: {
  awsCredentials: AwsCredentialsForList[] | undefined;
  awsRoles: AwsRole[] | undefined;
  githubInstalls: GithubConfig[] | undefined;
  s3Buckets: S3Bucket[] | undefined;
}): RequirementsMap => ({
  aws: {
    ok: [...(awsCredentials ?? []), ...(awsRoles ?? [])].some(
      ({ status }) => status === 'active',
    ),
    title: 'integrations.categories.publicCloud.aws',
    id: 'aws',
    logoUrl: awsLogo,
    available: true,
    to: '/integrations/aws',
  },
  s3Buckets: {
    ok: (s3Buckets ?? []).some(
      b => b.syncStatus === 'synced' && b.accessStatus === 'active',
    ),
    title: 'integrations.categories.terraformBackend.s3',
    id: 'terraformBackend',
    logoUrl: s3Logo,
    available: true,
    to: '/integrations/terraformBackend',
  },
  github: {
    ok: (githubInstalls ?? []).length > 0,
    title: 'integrations.categories.scm.github',
    id: 'code',
    logoUrl: githubLogo,
    available: true,
    onClick: () => {
      window.location.href = `https://github.com/login/oauth/authorize?client_id=${ViteEnv.VITE_GITHUB_CLIENT_ID}&scope=read:user,user:email`;
    },
  },
  monitoring: {
    ok: false,
    title: 'integrations.categories.monitoring.title',
    id: 'monitoring',
    logoUrl: datadogLogo,
    available: false,
  },
  containers: {
    ok: false,
    title: 'integrations.categories.containers.title',
    id: 'containers',
    logoUrl: kubernetesLogo,
    available: false,
  },
});

export const getCatalog = (): CatalogMap => ({
  onboarding: {
    id: 'onboarding',
    title: 'catalog.categories.onboarding.title',
    questions: {
      coreServices: {
        id: 'coreServices',
        title: 'catalog.categories.onboarding.questions.coreServices',
        requirementIds: ['aws', 's3Buckets', 'github'],
      },
      regions: {
        id: 'regions',
        title: 'catalog.categories.onboarding.questions.regions',
        requirementIds: ['aws'],
      },
      externalTraffic: {
        id: 'externalTraffic',
        title: 'catalog.categories.onboarding.questions.externalTraffic',
        requirementIds: ['aws'],
      },
    },
  },
  infrastructureManagement: {
    id: 'infrastructureManagement',
    title: 'catalog.categories.infrastructureManagement.title',
    questions: {
      securityGroupImpact: {
        id: 'securityGroupImpact',
        title:
          'catalog.categories.infrastructureManagement.questions.securityGroupImpact',
        requirementIds: ['aws'],
      },
      terraformModule: {
        id: 'terraformModule',
        title:
          'catalog.categories.infrastructureManagement.questions.terraformModule',
        requirementIds: ['github'],
      },
    },
  },
  incidentResponse: {
    id: 'incidentResponse',
    title: 'catalog.categories.incidentResponse.title',
    questions: {
      dynamoDB: {
        id: 'dynamoDB',
        title: 'catalog.categories.incidentResponse.questions.dynamoDB',
        requirementIds: ['aws'],
      },
      s3Contact: {
        id: 's3Contact',
        title: 'catalog.categories.incidentResponse.questions.s3Contact',
        requirementIds: ['aws', 'github', 's3Buckets'],
      },
    },
  },
  accessManagement: {
    id: 'accessManagement',
    title: 'catalog.categories.accessManagement.title',
    questions: {
      rdsInstance: {
        id: 'rdsInstance',
        title: 'catalog.categories.accessManagement.questions.rdsInstance',
        requirementIds: ['aws'],
      },
      iamUsersAdmin: {
        id: 'iamUsersAdmin',
        title: 'catalog.categories.accessManagement.questions.iamUsersAdmin',
        requirementIds: ['aws'],
      },
    },
  },
  securityCompliance: {
    id: 'securityCompliance',
    title: 'catalog.categories.securityCompliance.title',
    questions: {
      sshPermissions: {
        id: 'sshPermissions',
        title: 'catalog.categories.securityCompliance.questions.sshPermissions',
        requirementIds: ['aws'],
      },
      iamPolicies: {
        id: 'iamPolicies',
        title: 'catalog.categories.securityCompliance.questions.iamPolicies',
        requirementIds: ['aws', 'github', 's3Buckets'],
      },
    },
  },
  networking: {
    id: 'networking',
    title: 'catalog.categories.networking.title',
    questions: {
      vpcPeering: {
        id: 'vpcPeering',
        title: 'catalog.categories.networking.questions.vpcPeering',
        requirementIds: ['aws'],
      },
      hardcodedIPs: {
        id: 'hardcodedIPs',
        title: 'catalog.categories.networking.questions.hardcodedIPs',
        requirementIds: ['aws', 'github', 's3Buckets'],
      },
    },
  },
  costOptimization: {
    id: 'costOptimization',
    title: 'catalog.categories.costOptimization.title',
    questions: {
      gpuInstances: {
        id: 'gpuInstances',
        title: 'catalog.categories.costOptimization.questions.gpuInstances',
        requirementIds: ['aws', 'github', 's3Buckets'],
      },
      EBSVolumes: {
        id: 'EBSVolumes',
        title: 'catalog.categories.costOptimization.questions.EBSVolumes',
        requirementIds: ['aws'],
      },
    },
  },
});
